import { useState } from 'react';
import { ArrowRight, Phone, Calendar, Mail, CheckCircle2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '../components/ui/dialog';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7a187c08-1566-4a5d-94a6-902036d37fa8/artifacts/oin9oc2t_Untitled%20design%20%2814%29.png";

const serviceDetails = {
  'Self-Service Laundry': {
    headline: 'Self-Service Laundry',
    tagline: 'A clean, comfortable space with everything you need.',
    image: 'https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/g16y2r3t_IMG_9675.png',
    description: `Our self-service laundromat is designed to make laundry day simple, comfortable, and convenient. We offer a variety of washer sizes to fit your needs, including top loaders (1 load), as well as 2-load, 4-load, and 6-load washers — perfect for anything from small loads to bulky items. We also have plenty of dryers available so you can finish your laundry quickly.`,
    amenities: [
      'Free WiFi',
      'Convenient on-site parking',
      'Complimentary coffee',
      'Comfortable seating area with TV',
      'Change machine on-site',
      'Debit/credit card for change',
      'Attendant on duty',
    ],
    extras: `Need supplies? We have laundry soap and other laundry products available for purchase, along with soda and other refreshments to make your visit even more enjoyable.`,
    closing: `Our goal is to provide a clean, welcoming space with everything you need for a smooth, stress-free laundry experience.`,
    machines: [
      { label: 'Top Loaders', size: '1 Load' },
      { label: 'Front Loaders', size: '2 Loads' },
      { label: 'Front Loaders', size: '4 Loads' },
      { label: 'Front Loaders', size: '6 Loads' },
    ],
  },
  'Wash, Dry & Fold': {
    headline: 'Wash, Dry & Fold',
    tagline: 'Skip laundry day — let us handle it for you.',
    image: 'https://images.unsplash.com/photo-1737065183310-aef762bd011c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMGZvbGRlZCUyMHRvd2VscyUyMGxhdW5kcnl8ZW58MHx8fHwxNzc2MDIxNjkzfDA&ixlib=rb-4.1.0&q=85',
    description: `Skip laundry day and let us handle it for you with our convenient Wash & Fold service. Simply drop off your laundry, and our attendants will professionally wash, dry, and neatly fold your items so they're ready to take home.\n\nWe separate lights and darks, use quality detergents, and handle your clothes with care. Your laundry will be returned fresh, clean, and organized — saving you time and effort.`,
    amenities: [
      'Contact us for pricing',
      'Same-day service when available',
      'Regular turnaround: 2–3 days',
      'Lights and darks separated',
      'Quality detergents used',
      'Items returned fresh & organized',
    ],
    extras: null,
    closing: `This service is perfect for busy families, professionals, and anyone who wants to simplify their routine. Stop by and let us take laundry off your to-do list!`,
    machines: null,
    notes: [
      'If you have special requests such as stain treatment, please separate those items and notify us at drop-off.',
      'Customers are responsible for items left in pockets. Please check all pockets prior to drop-off.',
      'If you would like certain items hung instead of folded, please let us know at drop-off. You may provide your own hangers.',
      'If you have special detergent requests (scent-free, specific brand), please inform us during drop-off or provide your own.',
    ],
  },
  'Pickup & Delivery': {
    headline: 'Free Pickup & Delivery',
    tagline: 'Take laundry off your to-do list completely.',
    image: 'https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/pc2fgoq4_IMG_2926.jpeg',
    description: `Take laundry off your to-do list completely with our convenient Pickup & Delivery service. We'll pick up your laundry from your home, professionally clean it, and deliver it back fresh, folded, and ready to use.\n\nWe separate lights and darks, use quality detergents, and handle your items with care to ensure everything comes back clean, fresh, and neatly organized.`,
    amenities: [
      'Contact us for pricing',
      'Same-day service when available',
      'Regular turnaround: 2–3 days',
      'Free within Mountain Home city limits',
      'Lights and darks separated',
      'Quality detergents used',
    ],
    extras: null,
    closing: `Enjoy the convenience of laundry without leaving your home — we'll handle the rest.`,
    machines: null,
    notes: [
      'Pickup & delivery must be scheduled in advance.',
      'If you have special requests such as stain treatment, please separate those items and notify us at pickup.',
      'Customers are responsible for items left in pockets. Please check all pockets before pickup.',
      'If you would like items hung instead of folded, please let us know at pickup. You may provide your own hangers.',
      'If you have special detergent requests (scent-free, specific brand), please let us know at pickup or provide your own.',
    ],
  },
  'Ironing & Pressing': {
    headline: 'Ironing & Pressing',
    tagline: 'Look sharp without the hassle.',
    image: 'https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/otfrmgum_IMG_3496.jpeg',
    description: `Look sharp without the hassle with our professional ironing and pressing service. We carefully press your clothing so you can enjoy a clean, crisp, and polished look without spending time at home doing it yourself.\n\nThis service is perfect for work uniforms, dress shirts, slacks, special occasion outfits, and more. We take care to handle each item properly to help maintain fabric quality and ensure a neat, professional finish.`,
    amenities: [
      'Contact us for pricing',
      'Work uniforms & dress shirts',
      'Slacks & trousers',
      'Special occasion outfits',
      'Fabric quality maintained',
      'Neat, professional finish',
      'Drop-off or pickup available',
    ],
    extras: null,
    closing: `Enjoy convenience and a professional finish — we'll make sure you look your best.`,
    machines: null,
    notes: [
      'Please let us know at drop-off which items need ironing/pressing.',
      'Items should be clearly separated for this service.',
      'Special instructions are welcome at drop-off.',
    ],
  },
  'Alterations': {
    headline: 'Alterations',
    tagline: 'Perfect fit, every time.',
    image: 'https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/kst4iadj_IMG_3495.jpeg',
    description: `We offer basic alterations and adjustments to keep your wardrobe fitting perfectly. From hemming pants to taking in seams, our alteration service is a convenient one-stop addition to your laundry visit.`,
    amenities: [
      'Hemming & shortening',
      'Seam adjustments',
      'Basic tailoring',
      'Convenient drop-off',
      'Quick turnaround',
    ],
    extras: null,
    closing: `Ask our attendant about alteration services during your next visit.`,
    machines: null,
  },
  'Commercial Laundry': {
    headline: 'Commercial Laundry',
    tagline: 'Reliable laundry solutions for businesses of all sizes.',
    image: 'https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/b5es0hlx_att.vBedXlPyhABcT1sYeX7VDQK148nUOQMXtIyqY5_qkaE.jpeg',
    description: `We proudly offer reliable laundry solutions for businesses of all sizes. Our Commercial Laundry service is perfect for hotels, Airbnb hosts, salons, gyms, restaurants, medical offices, and other organizations that need consistent, high-quality laundry care.\n\nWe handle towels, linens, uniforms, tablecloths, bedding, and more with professional care and attention to detail. We understand that your business depends on clean linens and uniforms, so we focus on reliability, consistency, and quality service you can count on.`,
    amenities: [
      'Contact us for pricing',
      'Regular scheduled pickup & delivery',
      'Fast turnaround options',
      'Hotels & Airbnb / VRBO hosts',
      'Salons, gyms & restaurants',
      'Medical offices & clinics',
      'Towels, linens, uniforms & bedding',
    ],
    extras: null,
    closing: `To set up a commercial account, please contact us to discuss your laundry needs and frequency.`,
    machines: null,
    notes: null,
  },
};

export default function ServiceDetailModal({ serviceName, open, onClose }) {
  const detail = serviceDetails[serviceName];
  if (!detail) return null;

  const scrollToContact = () => {
    onClose();
    setTimeout(() => {
      const el = document.querySelector('#contact');
      if (el) {
        const offset = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0 rounded-2xl" data-testid="service-detail-modal">
        {/* Hero image */}
        <div className="relative h-48 sm:h-56 overflow-hidden rounded-t-2xl">
          <img
            src={detail.image}
            alt={detail.headline}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-white rounded-full p-0.5 shadow-md">
                <img src={LOGO_URL} alt="" className="w-7 h-7 object-contain" />
              </div>
              <span className="text-white/80 text-xs font-medium uppercase tracking-wider">Poly Clean Center</span>
            </div>
            <DialogHeader className="p-0">
              <DialogTitle className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight">
                {detail.headline}
              </DialogTitle>
              <DialogDescription className="text-white/80 text-sm mt-1">
                {detail.tagline}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 space-y-6">
          <p className="text-slate-600 text-base leading-relaxed">
            {detail.description}
          </p>

          {/* Machine sizes (Self-Service only) */}
          {detail.machines && (
            <div>
              <h4 className="font-heading font-bold text-base text-poly-blue-dark mb-3">Machine Sizes</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {detail.machines.map((m, i) => (
                  <div key={i} className="bg-sky-50 rounded-xl p-3 text-center border border-sky-100">
                    <span className="font-heading font-bold text-lg text-poly-blue block">{m.size}</span>
                    <span className="text-slate-500 text-xs">{m.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Amenities / Features */}
          <div>
            <h4 className="font-heading font-bold text-base text-poly-blue-dark mb-3">
              {serviceName === 'Commercial Laundry' ? 'Industries We Serve' : 'Features & Amenities'}
            </h4>
            <div className="grid sm:grid-cols-2 gap-2">
              {detail.amenities.map((item, i) => (
                item === 'Contact us for pricing' ? (
                  <button
                    key={i}
                    onClick={scrollToContact}
                    className="flex items-center gap-2 text-sm text-poly-orange font-semibold hover:text-orange-600 transition-colors cursor-pointer text-left"
                  >
                    <CheckCircle2 className="w-4 h-4 text-poly-orange shrink-0" />
                    Contact Us for Pricing &rarr;
                  </button>
                ) : (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-poly-green shrink-0" />
                    {item}
                  </div>
                )
              ))}
            </div>
          </div>

          {detail.extras && (
            <p className="text-slate-600 text-sm leading-relaxed bg-poly-bg rounded-xl p-4 border border-slate-100">
              {detail.extras}
            </p>
          )}

          {/* Please Note section */}
          {detail.notes && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="font-heading font-bold text-sm text-amber-800 mb-2">Please Note:</h4>
              <ul className="space-y-1.5">
                {detail.notes.map((note, i) => (
                  <li key={i} className="text-amber-700 text-xs leading-relaxed flex gap-2">
                    <span className="text-amber-400 mt-0.5 shrink-0">&#8226;</span>
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-slate-700 text-sm leading-relaxed font-medium">
            {detail.closing}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={scrollToContact}
              className="inline-flex items-center justify-center gap-2 bg-poly-orange hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-full transition-all hover:scale-105 active:scale-95"
              data-testid="service-modal-contact-btn"
            >
              <Calendar className="w-4 h-4" />
              {serviceName === 'Commercial Laundry' ? 'Request a Quote' : 'Schedule Service'}
            </button>
            <a
              href="tel:8704254295"
              className="inline-flex items-center justify-center gap-2 border-2 border-poly-blue text-poly-blue font-semibold px-6 py-3 rounded-full hover:bg-poly-blue hover:text-white transition-all"
              data-testid="service-modal-call-btn"
            >
              <Phone className="w-4 h-4" />
              Call (870) 425-4295
            </a>
            <a
              href="mailto:polyclean2024@gmail.com"
              className="inline-flex items-center justify-center gap-2 border border-slate-200 text-slate-600 font-medium px-6 py-3 rounded-full hover:bg-slate-50 transition-all text-sm"
              data-testid="service-modal-email-btn"
            >
              <Mail className="w-4 h-4" />
              Email Us
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
